import { MIN_OPTION_COUNT } from '@cavaquinho/shared';
import { Field } from '../../ui/primitives/Field.tsx';
import { Select } from '../../ui/primitives/Select.tsx';
import { violationsFor, type FieldViolation } from './validation.js';

/**
 * `visibleAnswers` — how many of the authored answers one prompt shows, the
 * correct one always among them. Shared by every type that authors a set of
 * answers (`note-identification/ConfigFields.tsx`, `QuestionOptionsFields`).
 *
 * "Todas" is the absent value rather than the current count, so an Exercise
 * showing every answer keeps doing so when one is added — which is also what
 * an Exercise authored before this field existed has always meant.
 */
interface VisibleAnswersFieldProps {
  value: number | undefined;
  /** How many answers are authored: the most that can be shown. */
  answerCount: number;
  violations: readonly FieldViolation[];
  onChange: (next: number | undefined) => void;
}

const ALL = 'all';

export function VisibleAnswersField({
  value,
  answerCount,
  violations,
  onChange,
}: VisibleAnswersFieldProps) {
  const error = violationsFor(violations, 'config.visibleAnswers')[0]?.message;
  const selected = value === undefined || value >= answerCount ? ALL : String(value);

  const counts: number[] = [];
  for (let count = MIN_OPTION_COUNT; count < answerCount; count += 1) counts.push(count);

  return (
    <Field
      size="compact"
      label="Respostas visíveis"
      htmlFor="visible-answers"
      hint="Quantas opções o aluno vê em cada tentativa. A correta está sempre entre elas, e a ordem muda a cada vez."
      error={error}
    >
      <Select
        id="visible-answers"
        size="compact"
        value={selected}
        invalid={error !== undefined}
        describedBy={error ? 'visible-answers-error' : 'visible-answers-hint'}
        options={[
          { value: ALL, label: `Todas (${answerCount})` },
          ...counts.map((count) => ({ value: String(count), label: String(count) })),
        ]}
        onChange={(next) => onChange(next === ALL ? undefined : Number(next))}
      />
    </Field>
  );
}

/**
 * Keeps a chosen count meaningful after the answers change: one that no
 * longer fits falls back to showing every answer.
 */
export function fitVisibleAnswers(
  visibleAnswers: number | undefined,
  answerCount: number,
): number | undefined {
  return visibleAnswers !== undefined && visibleAnswers < answerCount ? visibleAnswers : undefined;
}
